"use client";

import type React from "react";
import { useState, useCallback, useEffect } from "react";
import { Box, Select, Textarea, Text, useToast } from "@chakra-ui/react";
import {
  agentService,
  type RetellLLM,
  type UpdateRetellLLMParams,
} from "../../services/agentService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

interface WelcomeMessageSelectProps {
  initialLLM: RetellLLM;
  agentId: string;
  onLLMUpdate: (updatedLLM: RetellLLM) => void;
}

export const WelcomeMessageSelect: React.FC<WelcomeMessageSelectProps> = ({
  initialLLM,
  agentId,
  onLLMUpdate,
}) => {
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const [welcomeMessageType, setWelcomeMessageType] =
    useState<string>("user_initiates");
  const [welcomeMessage, setWelcomeMessage] = useState<string>("");

  // Set local state from the initial LLM
  useEffect(() => {
    if (!initialLLM) return;

    if (initialLLM.begin_message === "") {
      setWelcomeMessageType("user_initiates");
      setWelcomeMessage("");
    } else if (initialLLM.begin_message == null) {
      setWelcomeMessageType("dynamic");
      setWelcomeMessage("");
    } else {
      setWelcomeMessageType("defined");
      setWelcomeMessage(initialLLM.begin_message);
    }
  }, [initialLLM]);

  const handleWelcomeMessageChange = useCallback(
    async (newType: string, newMessage?: string) => {
      setWelcomeMessageType(newType);
      console.log(newMessage);

      let newValue: string | null;
      if (newType === "user_initiates") {
        newValue = "";
      } else if (newType === "defined") {
        newValue = newMessage ?? "";
      } else {
        newValue = null;
      }

      setWelcomeMessage(newValue ?? "");

      try {
        const params: UpdateRetellLLMParams = {
          agent_id: agentId,
          begin_message: newValue,
        };
        const updatedLLM = await agentService.updateRetellLLM(
          initialLLM.llm_id,
          params
        );
        onLLMUpdate(updatedLLM);

        toast({
          title: "Welcome message updated",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        toast({
          title: "Error updating welcome message",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      }
    },
    [initialLLM, agentId, onLLMUpdate, toast]
  );

  return (
    <Box>
      <Text color={isDark ? "gray.400" : "gray.600"} fontSize="sm" mb={2}>
        Welcome Message
      </Text>
      <Select
        size="sm"
        bg={isDark ? "gray.800" : "white"}
        border="none"
        borderColor={isDark ? "gray.600" : "gray.300"}
        color={isDark ? "white" : "gray.800"}
        value={welcomeMessageType}
        onChange={(e) =>
          e.target.value !== "defined"
            ? handleWelcomeMessageChange(e.target.value)
            : handleWelcomeMessageChange(
                e.target.value,
                "Hello, how can I help you today?"
              )
        }
      >
        <option
          value="user_initiates"
          style={{
            background: isDark ? "#2D3748" : "white",
            color: isDark ? "white" : "black",
          }}
        >
          User Initiates: AI remains silent until users speak first.
        </option>
        <option
          value="dynamic"
          style={{
            background: isDark ? "#2D3748" : "white",
            color: isDark ? "white" : "black",
          }}
        >
          AI Initiates: AI begins with a dynamic begin message.
        </option>
        <option
          value="defined"
          style={{
            background: isDark ? "#2D3748" : "white",
            color: isDark ? "white" : "black",
          }}
        >
          AI Initiates: AI begins with your defined begin message.
        </option>
      </Select>

      {welcomeMessageType === "defined" && (
        <Textarea
          sx={
            isDark
              ? {
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#2D3748", // Dark track color
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#4A5568", // Dark thumb color
                    borderRadius: "2px",
                  },
                  // For Firefox
                  scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                  _hover: {
                    scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                  },
                }
              : {
                  "&::-webkit-scrollbar": {
                    height: "4px",
                  },
                  "&::-webkit-scrollbar-button": {
                    display: "none",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#fff",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#fff",
                    borderRadius: "2px",
                  },
                  scrollbarColor: "rgb(220, 220, 220) white",
                  _hover: {
                    scrollbarColor: "rgb(220, 220, 220) white",
                  },
                }
          }
          mt={2}
          rows={1}
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
          onBlur={(e) => handleWelcomeMessageChange("defined", e.target.value)}
          placeholder="Add your defined begin message..."
          size="sm"
          bg={isDark ? "gray.800" : "white"}
          borderColor={isDark ? "gray.600" : "gray.300"}
          color={isDark ? "white" : "gray.800"}
          border="none"
          _focus={{ border: "none", boxShadow: "none" }}
        />
      )}
    </Box>
  );
};
